import { Alert, Button, Card, Form, Input, Space, Tag, Typography } from "antd";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type { BargainPageResult, BargainRow, ConsignmentQuery } from "./types";

const defaultQuery: ConsignmentQuery = { page: 1, size: 20 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const bargainStateLabels: Record<number, string> = { 1: "进行中", 2: "已完成", 3: "已结束" };
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "审核失败",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type BargainFilterValues = {
    keyword?: string;
};

type BargainSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

export interface BargainPageProps {
    assignBargain?: (id: number | string, handlerId: number) => Promise<void>;
    closeBargain?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfBargain?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<BargainSpecialist[]>;
    loadBargains?: (query: ConsignmentQuery) => Promise<BargainPageResult>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 捡漏商品分页接口。 */
export async function loadBargains(query: ConsignmentQuery): Promise<BargainPageResult> {
    const response = await request.service.get<BargainPageResult>("/admin/product-bargain/paging", { params: query });
    return response.data;
}

/** 复用 Vue 捡漏商品权重更新接口。 */
export async function updateBargainWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-bargain/weight/${params.id}`, params);
}

/** 复用 Vue 捡漏商品下架接口。 */
export async function downShelfBargain(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-bargain/down-shelf/${id}`);
}

/** 复用 Vue 捡漏商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeBargain(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-bargain/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 捡漏专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<BargainSpecialist[]> {
    const response = await request.service.get<BargainSpecialist[]>(
        `/admin/product-bargain/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 捡漏商品指派接口。 */
export async function assignBargain(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-bargain/assign/${id}/${handlerId}`);
}

/** 将 Vue 捡漏商品列表返回的捡漏、商品和发布状态枚举统一为数字。 */
function normalizeBargainRows(rows?: BargainRow[]): BargainRow[] {
    return (rows || []).map(row => ({
        ...row,
        productBargain: {
            ...row.productBargain,
            bargainState: row.productBargain.bargainState == null ? undefined : Number(row.productBargain.bargainState),
            state: row.productBargain.state == null ? undefined : Number(row.productBargain.state),
            publishState: row.productBargain.publishState == null ? undefined : Number(row.productBargain.publishState)
        }
    }));
}

/** 捡漏商品列表，保留 Vue 首屏字段、权限和复制入口。 */
export function BargainPage({
    assignBargain: assign = assignBargain,
    closeBargain: close = closeBargain,
    downShelfBargain: removeFromShelf = downShelfBargain,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadBargains: load = loadBargains,
    updateWeight: saveWeight = updateBargainWeight,
    permissions
}: BargainPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<BargainRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<BargainRow | null>(null);
    const [specialists, setSpecialists] = useState<BargainSpecialist[]>([]);
    const [specialistKeyword, setSpecialistKeyword] = useState("");
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [query, setQuery] = useState<ConsignmentQuery>(defaultQuery);
    const [filterForm] = Form.useForm<BargainFilterValues>();

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeBargainRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("捡漏商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将旧 Vue 商品编号/标题筛选转换为捡漏分页接口的 `keyword` 参数。
    const submitFilters = ({ keyword }: BargainFilterValues) => {
        setErrorMessage(null);
        setQuery({ ...defaultQuery, ...(keyword ? { keyword } : {}) });
    };

    // 清空关键词并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    // 状态动作成功后沿用当前 Vue 分页参数重新加载列表。
    const refresh = async () => {
        const result = await load(query);
        setItems(normalizeBargainRows(result.list));
        setTotal(result.total || 0);
    };

    const handleClose = async (row: BargainRow) => {
        const id = row.productBargain.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前捡漏商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("捡漏商品关闭失败");
        }
    };

    const handleDownShelf = async (id?: number | string) => {
        if (id == null) return;
        try {
            await removeFromShelf(id);
            await refresh();
        } catch {
            // 下架接口或刷新失败时保留当前捡漏商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("捡漏商品下架失败");
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: BargainRow) => {
        setAssignTarget(row);
        setSpecialistKeyword("");
        setSelectedHandlerId(row.productBargain.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async () => {
        if (!assignTarget || selectedHandlerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productBargain.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, selectedHandlerId);
            setAssignTarget(null);
            await refresh();
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 捡漏商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<BargainRow>[] = [
        {
            key: "id",
            label: "商品编号",
            prop: "productBargain",
            render: ({ row }) => (
                <Button
                    aria-label="查看捡漏商品详情"
                    type="link"
                    onClick={() => void navigate(`/productManagement/bargain/detail?id=${row.productBargain.id || ""}`)}
                >
                    {row.productBargain.id || "-"}
                </Button>
            )
        },
        {
            key: "weight",
            label: "权重值",
            prop: "productBargain",
            render: ({ row }) => (
                <ProductWeightInput
                    aria-label="捡漏商品权重值"
                    controls={false}
                    max={10000}
                    min={0}
                    precision={0}
                    defaultValue={row.productBargain.weight ?? ""}
                    onBlur={event => {
                        const input = event.currentTarget as HTMLInputElement;
                        const weight = Number(input.value);
                        if (row.productBargain.id == null || input.value === "" || !Number.isFinite(weight)) return;
                        void saveWeight({ id: row.productBargain.id, weight });
                    }}
                />
            )
        },
        {
            key: "title",
            label: "主图/标题",
            prop: "productBargain",
            render: ({ row }) => (
                <Space>
                    {row.mainFile?.path ? (
                        <img alt="捡漏商品主图" className="h-8 w-8 object-contain" src={row.mainFile.path} />
                    ) : (
                        <span className="product-image-placeholder">暂无图片</span>
                    )}
                    <span>{row.productBargain.title || "-"}</span>
                </Space>
            )
        },
        {
            key: "productType",
            label: "品类",
            prop: "productType",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "-"
        },
        {
            key: "supplier",
            label: "供应商",
            prop: "productBargain",
            render: ({ row }) =>
                row.productBargain.supplierEnterprise?.enterpriseName ||
                row.productBargain.supplierEnterprise?.supplierName ||
                "-"
        },
        {
            key: "bargainUnitPrice",
            label: "捡漏单价（元/吨）",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.bargainUnitPrice ?? "-"
        },
        {
            key: "bargainTon",
            label: "捡漏吨数（吨）",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.bargainTon ?? "-"
        },
        {
            key: "totalCost",
            label: "捡漏总价（元）",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.totalCost ?? "-"
        },
        {
            key: "bargainStartTime",
            label: "捡漏开始时间",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.bargainStartTime || "-"
        },
        {
            key: "bargainEndTime",
            label: "捡漏截止时间",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.bargainEndTime || "-"
        },
        {
            key: "handlerName",
            label: "商品专员",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.handlerName || "-"
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "productBargain",
            render: ({ row }) => row.productBargain.updatedTime || "-"
        },
        {
            key: "bargainState",
            label: "捡漏状态",
            prop: "productBargain",
            render: ({ row }) => <Tag>{bargainStateLabels[row.productBargain.bargainState || 0] || "-"}</Tag>
        },
        {
            key: "state",
            label: "上架状态",
            prop: "productBargain",
            render: ({ row }) => <Tag>{productStateLabels[row.productBargain.state || 0] || "-"}</Tag>
        },
        {
            key: "publishState",
            label: "发布状态",
            prop: "productBargain",
            render: ({ row }) => <Tag>{publishStateLabels[row.productBargain.publishState || 0] || "-"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            prop: "productBargain",
            render: ({ row }) => {
                const { bargainState, id, publishState, state } = row.productBargain;
                const showAudit = publishState === 1;
                const showEdit = state === 2 && [1, 2, 4, 5].includes(publishState || 0);
                const showDown = state === 1 && [3, 4].includes(publishState || 0);
                const showClose = state === 2 && [1, 2, 4, 5].includes(publishState || 0);
                const showCopy = publishState === 6 && state === 2 && bargainState === 2;
                const showAssign = !(state === 2 && [5, 6].includes(publishState || 0));

                return (
                    <Space wrap>
                        {canManage && showAssign ? (
                            <Button
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </Button>
                        ) : null}
                        {canManage && showAudit ? (
                            <Button
                                aria-label="审核捡漏商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/bargain/audit?id=${id || ""}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        {canDeal && showEdit ? (
                            <Button
                                aria-label="编辑捡漏商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/bargain/edit?id=${id || ""}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClosePermission && showClose ? (
                            <Button
                                aria-label="关闭捡漏商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void handleClose(row)}
                            >
                                关闭
                            </Button>
                        ) : null}
                        {canDeal && showDown ? (
                            <Button
                                aria-label="下架捡漏商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void handleDownShelf(id)}
                            >
                                下架
                            </Button>
                        ) : null}
                        {canDeal && showCopy ? (
                            <Button
                                aria-label="复制捡漏商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/bargain/add?copyId=${id || ""}`)}
                            >
                                复制
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="bargain-page" className="flex h-full min-h-full flex-col">
            <ZhDialog
                open={Boolean(assignTarget)}
                title="变更指派商品专员"
                width={842}
                onCancel={() => setAssignTarget(null)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignTarget(null)}>取消</Button>
                        <Button
                            aria-label="确认指派"
                            loading={assignLoading}
                            type="primary"
                            onClick={() => void handleAssign()}
                        >
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <Space.Compact block>
                    <Input
                        aria-label="专员姓名"
                        placeholder="请输入姓名"
                        value={specialistKeyword}
                        onChange={event => setSpecialistKeyword(event.target.value)}
                    />
                    <Button type="primary" onClick={() => void searchSpecialists(specialistKeyword)}>
                        查询专员
                    </Button>
                </Space.Compact>
                {assignError ? <p role="alert">{assignError}</p> : null}
                <div aria-label="专员列表" className="max-h-72 overflow-auto" role="radiogroup">
                    {specialists.map(specialist => {
                        const personId = specialist.user.personId;
                        if (personId == null) return null;
                        return (
                            <label key={personId} className="mt-3 block">
                                <input
                                    aria-label={specialist.user.name || String(personId)}
                                    checked={selectedHandlerId === personId}
                                    name="bargain-handler"
                                    type="radio"
                                    onChange={() => setSelectedHandlerId(personId)}
                                />
                                <span className="ml-2">
                                    <span>{specialist.user.name || "未命名专员"}</span> (
                                    {specialist.user.employeeId || "-"})
                                </span>
                            </label>
                        );
                    })}
                </div>
            </ZhDialog>
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<BargainRow>
                    data={items}
                    columns={columns}
                    emptyText="暂无捡漏商品"
                    pagination={false}
                    rowKey={row => String(row.productBargain.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                捡漏商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canDeal ? (
                                    <Button
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/productManagement/bargain/add")}
                                    >
                                        新增捡漏商品
                                    </Button>
                                ) : null}
                                <Form.Item name="keyword">
                                    <Input placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Button
                                    aria-label="搜索"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 捡漏商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default BargainPage;
